const isBrowser = typeof window !== "undefined";
const channel = process.env.REACT_APP_DEFAULT_CHANNEL;

const WEBVIEW = `${channel}-marketplace`;
const FUNCIONALIDADES = "marketplace";
const DEFAULT_CURRENCY = "BRL";
const DEFAULT_AFFILIATION = "rock-encantech";
const AFFILIATION_DISPLAY = "rock-encantech";

export const MARKETPLACE_TAG_IDS = Object.freeze({
  // Não-ecommerce
  click: `${channel}.marketplace.01`,
  clickFilter: `${channel}.marketplace.04`,
  clickFilterClose: `${channel}.marketplace.05`,
  clickList: `${channel}.marketplace.03`,
  clickMyAccount: `${channel}.marketplace.06`,
  clickAddressForm: `${channel}.marketplace.07`,
  clickCardForm: `${channel}.marketplace.08`,
  callback: `${channel}.marketplace.09`,
  clickMyRequests: `${channel}.marketplace.10`,
  clickPolicy: `${channel}.marketplace.11`,
  clickSearch: `${channel}.marketplace.12`,
  search: `${channel}.marketplace.13`,
  clickBuy: `${channel}.marketplace.14`,
  clickOrderResume: `${channel}.marketplace.15`,
  clickCheckoutAddresses: `${channel}.marketplace.16`,
  clickResumoCompra: `${channel}.marketplace.17`,
  clickOrderStatus: `${channel}.marketplace.18`,
  login: `${channel}.marketplace.19`,

  // Ecommerce
  banner_impression: `${channel}.marketplace.ecommerce.1`,
  banner_click: `${channel}.marketplace.ecommerce.2`,
  view_item_list: `${channel}.marketplace.ecommerce.3`,
  add_to_wishlist: `${channel}.marketplace.ecommerce.4`,
  select_item: `${channel}.marketplace.ecommerce.5`,
  view_item: `${channel}.marketplace.ecommerce.6`,
  add_to_cart: `${channel}.marketplace.ecommerce.7`,
  view_cart: `${channel}.marketplace.ecommerce.8`,
  remove_from_cart: `${channel}.marketplace.ecommerce.9`,
  begin_checkout: `${channel}.marketplace.ecommerce.10`,
  add_shipping_info: `${channel}.marketplace.ecommerce.11`,
  add_payment_info: `${channel}.marketplace.ecommerce.12`,
  purchase: `${channel}.marketplace.ecommerce.13`,
});

const withDefaults = (value, fallback) =>
  value === undefined || value === null || value === "" ? fallback : value;

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

export const normalizeForTracking = (str) => {
  if (str == null || typeof str !== "string") return "";
  return (
    str
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/gi, "")
      .toLowerCase()
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || ""
  );
};

const marketplaceBase = (tagId) => ({
  id_da_tag: tagId,
  webview: WEBVIEW,
});

const resolveBrandLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.label ?? value.name ?? "";
  }
  return String(value);
};

const normalizeCartLineItem = (line) => {
  if (!line || typeof line !== "object") return line;
  const product = line.item && typeof line.item === "object" ? line.item : line;
  const productId = product.uuid ?? product.id ?? line.uuid ?? line.id;

  return {
    ...product,
    quantity: line.quantity ?? product.quantity ?? 1,
    price: line.price ?? product.price,
    discount: line.discount ?? product.discount,
    uuid: productId,
    sku: product.sku ?? line.sku,
    variations: product.variations ?? line.variations,
    variation: product.variation ?? line.variation,
    attributes: product.attributes ?? line.attributes,
    item_variant: product.item_variant ?? line.item_variant,
    variation_label: product.variation_label ?? line.variation_label,
    product_variation: product.product_variation ?? line.product_variation,
    seller:
      line.seller ??
      product.seller?.label ??
      product.seller_name ??
      product.seller,
    brand:
      line.brand ??
      product.brand?.label ??
      product.brand?.name ??
      product.brand_name ??
      product.brand,
    category:
      line.category ??
      product.category_label ??
      product.category_name ??
      product.categories?.[0]?.label ??
      product.categories?.[0]?.name,
  };
};

const isVariationAttributeMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const entries = Object.values(value);
  return (
    entries.length > 0 &&
    entries.every((entry) => entry && typeof entry === "object" && "value" in entry)
  );
};

export const formatItemVariant = (variationSet) => {
  if (!variationSet || typeof variationSet !== "object") return "";
  if (Array.isArray(variationSet)) {
    return variationSet
      .map((entry) => entry?.value ?? entry?.label ?? entry?.name ?? entry)
      .filter(Boolean)
      .join(" / ");
  }
  return Object.values(variationSet)
    .map((v) => (v && typeof v === "object" ? v.value : v))
    .filter(Boolean)
    .join(" / ");
};

const ITEM_VARIANT_CACHE_KEY = "wl_marketplace_item_variants";

export const cacheMarketplaceItemVariant = (itemId, itemVariant) => {
  if (!isBrowser || itemId == null || itemId === "") return;
  const variant = String(itemVariant ?? "").trim();
  if (!variant) return;
  try {
    const cache = JSON.parse(sessionStorage.getItem(ITEM_VARIANT_CACHE_KEY) || "{}");
    cache[String(itemId)] = variant;
    sessionStorage.setItem(ITEM_VARIANT_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore storage errors
  }
};

const getCachedMarketplaceItemVariant = (...ids) => {
  if (!isBrowser) return "";
  try {
    const cache = JSON.parse(sessionStorage.getItem(ITEM_VARIANT_CACHE_KEY) || "{}");
    for (const id of ids) {
      if (id == null || id === "") continue;
      const variant = String(cache[String(id)] ?? "").trim();
      if (variant) return variant;
    }
  } catch {
    // ignore storage errors
  }
  return "";
};

const resolveVariationSetFromMap = (variations, productId) => {
  if (!variations || typeof variations !== "object" || Array.isArray(variations)) {
    return null;
  }

  const id = productId != null ? String(productId) : "";
  if (id && variations[id]) return variations[id];

  if (isVariationAttributeMap(variations)) return variations;

  const entries = Object.entries(variations);
  if (!entries.length) return null;

  if (entries.length === 1) {
    const [, onlyValue] = entries[0];
    if (onlyValue && typeof onlyValue === "object") return onlyValue;
  }

  if (id) {
    const matched = entries.find(([key]) => String(key) === id);
    if (matched) return matched[1];
  }

  const attributeMapEntry = entries.find(([, value]) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const values = Object.values(value);
    return (
      values.length > 0 &&
      values.every((entry) => entry && typeof entry === "object" && "value" in entry)
    );
  });
  if (attributeMapEntry) return attributeMapEntry[1];

  return null;
};

const extractItemVariantFromProduct = (product) => {
  if (!product || typeof product !== "object") return "";

  if (typeof product.item_variant === "string" && product.item_variant.trim()) {
    return product.item_variant.trim();
  }
  if (typeof product.variant === "string" && product.variant.trim()) {
    return product.variant.trim();
  }
  if (typeof product.variation_label === "string" && product.variation_label.trim()) {
    return product.variation_label.trim();
  }
  if (typeof product.product_variation === "string" && product.product_variation.trim()) {
    return product.product_variation.trim();
  }
  if (product.product_variation && typeof product.product_variation === "object") {
    const fromProductVariation = formatItemVariant(product.product_variation);
    if (fromProductVariation) return fromProductVariation;
  }
  if (Array.isArray(product.attributes) && product.attributes.length) {
    const fromAttributes = product.attributes
      .map((attr) => attr?.value ?? attr?.label ?? attr?.name)
      .filter(Boolean)
      .join(" / ");
    if (fromAttributes) return fromAttributes;
  }

  const productId = product.uuid ?? product.id;
  const variationSet = resolveVariationSetFromMap(product.variations, productId);
  if (variationSet) {
    const formatted = formatItemVariant(variationSet);
    if (formatted) return formatted;
  }

  if (Array.isArray(product.variation) && product.variation.length) {
    const fromVariationArray = formatItemVariant(product.variation);
    if (fromVariationArray) return fromVariationArray;
  }
  if (product.variation && typeof product.variation === "object") {
    const fromVariationObject = formatItemVariant(product.variation);
    if (fromVariationObject) return fromVariationObject;
  }

  return getCachedMarketplaceItemVariant(productId, product.sku);
};

export const getMarketplaceListItemVariant = extractItemVariantFromProduct;

const extractPointsFromProduct = (product) =>
  normalizeNumber(
    product?.product_price?.points_suggested ??
      product?.product_price?.points ??
      product?.product_price?.points_offer ??
      product?.price?.points?.offer ??
      product?.price?.points?.points ??
      product?.price?.points?.final ??
      product?.price?.safra?.offer ??
      product?.price?.safra?.final ??
      product?.price?.safra ??
      product?.points,
  );

export const getMarketplaceItemPoints = extractPointsFromProduct;

export const mapProductToEcommerceTrackingFields = (product = {}) => {
  if (!product || typeof product !== "object") return {};

  const productId = product.uuid ?? product.id;
  const listPrice = product.product_price;
  const brl = product.price?.BRL;

  const priceNum = listPrice
    ? Number(listPrice.price ?? 0)
    : Number(brl?.offer ?? brl?.price ?? 0);

  let discount = 0;
  if (listPrice) {
    discount = Math.max(
      0,
      Number(listPrice.price_suggested || 0) - Number(listPrice.price || 0),
    );
  } else if (brl?.price != null && brl?.offer != null) {
    discount = Math.max(0, parseFloat(brl.price) - parseFloat(brl.offer));
  } else if (product.discount != null) {
    discount = Number(product.discount) || 0;
  }

  return {
    itemId: productId ?? "",
    itemName: String(
      product.label ?? product.short_description ?? product.name ?? "",
    ).trim(),
    itemBrand: String(
      product.brand_name ??
        product.brand?.name ??
        product.seller_name ??
        product.seller?.label ??
        product.seller ??
        "",
    ).trim(),
    itemCategory: String(
      product.product_categories?.[0]?.category_name ??
        product.categories?.[0]?.name ??
        product.category_name ??
        product.category ??
        "",
    ).trim(),
    itemVariant: extractItemVariantFromProduct(product),
    points: extractPointsFromProduct(product),
    price: priceNum,
    discount,
    quantity: Number(product.quantity) || 1,
  };
};

const resolveEcommerceItemFields = (params = {}) => {
  const { product, ...explicit } = params;
  const source =
    product ||
    (explicit.uuid ||
    explicit.id ||
    explicit.product_price ||
    explicit.price?.BRL ||
    explicit.price?.points
      ? explicit.item && typeof explicit.item === "object"
        ? {
            ...explicit.item,
            quantity: explicit.quantity,
            price: explicit.price,
          }
        : explicit
      : null);

  const auto = source ? mapProductToEcommerceTrackingFields(source) : {};
  const explicitVariant =
    typeof explicit.itemVariant === "string" ? explicit.itemVariant.trim() : "";

  return {
    ...auto,
    ...explicit,
    itemId: explicit.itemId ?? auto.itemId,
    itemName: explicit.itemName ?? auto.itemName,
    itemBrand: explicit.itemBrand ?? auto.itemBrand,
    itemCategory: explicit.itemCategory ?? auto.itemCategory,
    price: explicit.price ?? auto.price,
    discount: explicit.discount ?? auto.discount,
    quantity: explicit.quantity ?? auto.quantity,
    itemVariant: explicitVariant || auto.itemVariant || "",
    points:
      explicit.points !== undefined && explicit.points !== null
        ? explicit.points
        : auto.points,
  };
};

/** Um item no formato ecommerce.items[] a partir de objeto do carrinho */
const toEcommerceItemFromCartItem = (item, index, coupon = "") => {
  const normalized = normalizeCartLineItem(item);
  const priceNum = Number(
    normalized.price?.BRL?.offer ??
      normalized.price?.BRL?.price ??
      normalized.price?.BRL?.final ??
      normalized.price ??
      0,

  );
  const quantity = Number(normalized.quantity) || 1;
  const ecommerceItem = {
    affiliation: AFFILIATION_DISPLAY,
    item_id: String(normalized.uuid ?? normalized.sku ?? normalized.id ?? ""),
    item_name: String(
      normalized.short_description ?? normalized.name ?? normalized.label ?? "",

    ).trim(),
    coupon: coupon || undefined,
    discount: Number(normalizeNumber(normalized.discount)) || 0,
    index,
    item_brand: String(
      resolveBrandLabel(normalized.seller) || resolveBrandLabel(normalized.brand) || "",
    ).trim(),
    item_category: String(
      normalized.category ?? normalized.category_label ?? "",
    ).trim(),
    price: priceNum,
    quantity,
  };

  const variant = extractItemVariantFromProduct(normalized);
  if (variant) ecommerceItem.item_variant = variant;

  const pointsNum = extractPointsFromProduct(normalized);
  if (pointsNum !== undefined) ecommerceItem.points = pointsNum;

  return ecommerceItem;
};

const toEcommerceItemFromParams = (params = {}) => {
  const resolved = resolveEcommerceItemFields(params);
  const {
    itemId,
    itemName,
    discount = 0,
    index = 0,
    itemBrand = "",
    itemCategory = "",
    itemVariant = "",
    points,
    price,
    quantity = 1,
    coupon = "",
  } = resolved;
  const priceNum = Number(price) ?? 0;
  const qty = Number(quantity) || 1;
  const item = {
    affiliation: AFFILIATION_DISPLAY,
    item_id: String(itemId ?? ""),
    item_name: String(itemName ?? "").trim(),
    coupon: coupon || undefined,
    discount: Number(discount) || 0,
    index,
    item_brand: String(itemBrand ?? "").trim(),
    item_category: String(itemCategory ?? "").trim(),
    price: priceNum,
    quantity: qty,
  };

  const variant = String(itemVariant ?? "").trim();
  if (variant) item.item_variant = variant;

  const pointsNum = normalizeNumber(points);
  if (pointsNum !== undefined) item.points = pointsNum;

  return item;
};

const marketplaceBaseWithScreen = (tagId, screenName) => ({
  ...marketplaceBase(tagId),
  Nome_Da_Tela: `marketplace-${screenName}`,
  funcionalidades: FUNCIONALIDADES,
});

const sumEcommerceItemsPoints = (items = []) =>
  Math.round(
    (Array.isArray(items) ? items : []).reduce(
      (sum, item) =>
        sum + (Number(item.points) || 0) * (Number(item.quantity) || 1),
      0,
    ),
  );

const pushEcommerceWithItems = (eventName, tagId, ecommerce, options = {}) => {
  if (!isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: eventName,
    ...marketplaceBase(tagId),
  };
  if (options.includeTotalPoints) {
    payload.points = sumEcommerceItemsPoints(ecommerce?.items);
  }
  payload.ecommerce = ecommerce;
  window.dataLayer.push(payload);
};

const sendMarketplaceEcommerceEvent = (eventName, tagId, ecommerce) => {
  if (!isBrowser) return;
  sendGA4Event(eventName, {
    ...marketplaceBase(tagId),
    points: sumEcommerceItemsPoints(ecommerce?.items),
    ecommerce,
  });
};

const toEcommerceListItem = (params = {}) => {
  const resolved = resolveEcommerceItemFields(params);
  const {
    itemId,
    itemName,
    itemListName = "",
    discount = 0,
    index = 0,
    itemBrand = "",
    itemCategory = "",
    itemVariant = "",
    points,
    price = 0,
    quantity = 1,
    coupon = "",
  } = resolved;

  const item = {
    affiliation: AFFILIATION_DISPLAY,
    item_id: String(itemId ?? ""),
    item_name: String(itemName ?? "").trim(),
    item_list_name: String(itemListName ?? "").trim(),
    coupon: coupon || undefined,
    discount: Number(normalizeNumber(discount)) || 0,
    index,
    item_brand: String(itemBrand ?? "").trim(),
    item_category: String(itemCategory ?? "").trim(),
    price: Number(price) ?? 0,
    quantity: Number(quantity) || 1,
  };

  const variant = String(itemVariant ?? "").trim();
  if (variant) item.item_variant = variant;

  const pointsNum = normalizeNumber(points);
  if (pointsNum !== undefined) item.points = pointsNum;

  return item;
};

export const sendGA4Event = (eventName, eventParams = {}) => {
  if (!isBrowser) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventParams,
  });
  if (process.env.NODE_ENV === "development") {
    console.log("[GTM][GA4]", eventName, eventParams);
  }
};

export const sendGA4EcommerceEvent = (
  eventName,
  ecommerce = {},
  eventParams = {},
) => {
  if (!isBrowser) return;

  window.dataLayer = window.dataLayer || [];
  sendGA4Event(eventName, {
    ecommerce,
    ...eventParams,
  });
};

export const getSearchScreenNameFromPath = (pathname) => {
  if (!pathname || typeof pathname !== "string") return "home";
  const path = pathname.replace(/^\/+/, "").toLowerCase();
  if (path === "loja" || path === "loja/") return "home";
  if (path.startsWith("loja/categories")) return "lista-categorias";
  if (path.startsWith("loja/brands/") && path.split("/").length > 3)
    return "vitrine-marcas";
  if (path.startsWith("loja/brands")) return "lista-marcas";
  if (path.startsWith("loja/category/")) return "vitrine-categorias";
  if (path.startsWith("loja/myaccount")) return "minha-conta";
  if (path.startsWith("loja/myrequests")) return "meus-pedidos";
  if (path.startsWith("loja/favorites")) return "favoritos";
  if (path.startsWith("loja/policy")) return "termos-e-condicoes";
  if (path.startsWith("loja/products/")) return "resultado-da-busca";
  if (path.startsWith("loja/search-hotels-result")) return "resultado-da-busca";
  if (path.startsWith("loja/search-cars-result")) return "resultado-da-busca";
  if (path.startsWith("loja/searchresulttravel")) return "resultado-da-busca";
  return "home";
};

export const trackClick = ({
  title,
  contentType,
  screenName,
  tagId,
  ...eventParams
}) => {
  const selectedTagId = tagId || MARKETPLACE_TAG_IDS.click;
  sendGA4Event("select_content", {
    elemento: "botao",
    custom_title: title,
    content_type: contentType,
    ...eventParams,
    ...marketplaceBaseWithScreen(selectedTagId, screenName),
  });
};

export const trackCartEvent = ({ title, screenName = "carrinho" }) => {
  trackClick({
    title,
    contentType: "checkout",
    screenName,
    tagId: MARKETPLACE_TAG_IDS.clickOrderResume,
  });
};

export const trackCheckoutAddressEvent = ({ title }) => {
  trackClick({
    title,
    contentType: "checkout",
    screenName: "checkout-enderecos",
    tagId: MARKETPLACE_TAG_IDS.clickCheckoutAddresses,
  });
};

export const trackResumoCompraEvent = ({ title }) => {
  trackClick({
    title,
    contentType: "checkout",
    screenName: "resumo-da-compra",
    tagId: MARKETPLACE_TAG_IDS.clickResumoCompra,
  });
};

export const trackOrderStatusEvent = ({ title, contentType, screenName }) => {
  trackClick({
    title,
    contentType,
    screenName,
    tagId: MARKETPLACE_TAG_IDS.clickOrderStatus,
  });
};

export const trackSearchEvent = ({ title, screenName }) => {
  trackClick({
    title,
    contentType: "busca",
    screenName: screenName || "home",
    tagId: MARKETPLACE_TAG_IDS.clickSearch,
  });
};

export const trackClickBuyEvent = ({ title, screenName }) => {
  trackClick({
    title,
    contentType: "busca",
    screenName: screenName || "home",
    tagId: MARKETPLACE_TAG_IDS.clickBuy,
  });
};

export const trackPdpShippingQuoteClick = ({ title, screenName }) => {
  trackClick({
    title,
    contentType: "pdp",
    screenName,
    tagId: MARKETPLACE_TAG_IDS.click,
  });
};

export const trackSearch = (searchTerm, eventParams = {}) => {
  sendGA4Event("search", {
    search_term: searchTerm,
    event_category: "marketplace",
    event_label: "search",
    ...eventParams,
  });
};

export const trackViewItemList = ({
  itemListId,
  itemListName,
  items = [],
  currency = "BRL",
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "view_item_list",
    {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items,
    },
    {
      currency,
      event_category: "marketplace",
      event_label: "view_item_list",
      ...eventParams,
    },
  );
};

export const trackSelectItem = ({
  itemListId,
  itemListName,
  items = [],
  currency = "BRL",
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "select_item",
    {
      item_list_id: itemListId,
      item_list_name: itemListName,
      items,
    },
    {
      currency,
      event_category: "marketplace",
      event_label: "select_item",
      ...eventParams,
    },
  );
};

export const trackViewItem = ({
  items = [],
  currency = "BRL",
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "view_item",
    { items },
    {
      currency,
      event_category: "marketplace",
      event_label: "view_item",
      ...eventParams,
    },
  );
};

export const trackAddToCart = ({
  items = [],
  currency = "BRL",
  value,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "add_to_cart",
    { items, value, currency },
    {
      currency,
      value,
      event_category: "marketplace",
      event_label: "add_to_cart",
      ...eventParams,
    },
  );
};

export const trackRemoveFromCart = ({
  items = [],
  currency = "BRL",
  value,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "remove_from_cart",
    { items, value, currency },
    {
      currency,
      value,
      event_category: "marketplace",
      event_label: "remove_from_cart",
      ...eventParams,
    },
  );
};

export const trackViewCart = ({
  items = [],
  currency = "BRL",
  value,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "view_cart",
    { items, value, currency },
    {
      currency,
      value,
      event_category: "marketplace",
      event_label: "view_cart",
      ...eventParams,
    },
  );
};

export const trackBeginCheckout = ({
  items = [],
  currency = "BRL",
  value,
  coupon,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "begin_checkout",
    { items, value, currency, coupon },
    {
      currency,
      value,
      coupon,
      event_category: "marketplace",
      event_label: "begin_checkout",
      ...eventParams,
    },
  );
};

export const trackAddShippingInfo = ({
  items = [],
  currency = "BRL",
  value,
  shippingTier,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "add_shipping_info",
    { items, value, currency, shipping_tier: shippingTier },
    {
      currency,
      value,
      shipping_tier: shippingTier,
      event_category: "marketplace",
      event_label: "add_shipping_info",
      ...eventParams,
    },
  );
};

export const trackAddPaymentInfo = ({
  items = [],
  currency = "BRL",
  value,
  paymentType,
  coupon,
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "add_payment_info",
    { items, value, currency, payment_type: paymentType, coupon },
    {
      currency,
      value,
      payment_type: paymentType,
      coupon,
      event_category: "marketplace",
      event_label: "add_payment_info",
      ...eventParams,
    },
  );
};

export const trackPurchase = ({
  transactionId,
  value,
  tax,
  shipping,
  currency = "BRL",
  coupon,
  affiliation,
  items = [],
  ...eventParams
}) => {
  sendGA4EcommerceEvent(
    "purchase",
    {
      transaction_id: transactionId,
      value,
      tax,
      shipping,
      currency,
      coupon,
      affiliation,
      items,
    },
    {
      currency,
      value,
      transaction_id: transactionId,
      event_category: "marketplace",
      event_label: "purchase",
      ...eventParams,
    },
  );
};

export const trackMarketplaceLogin = ({
  method = "autenticação",
  screenName = "home",
} = {}) => {
  sendGA4Event("login", {
    method,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.login, screenName),
  });
};

export const trackCallbackMarketplace = ({
  status = "sucesso",
  contentType,
  screenName = "minha-conta",
} = {}) => {
  sendGA4Event("callback", {
    status,
    content_type: contentType,
    Nome_Da_Tela: screenName,
    funcionalidades: FUNCIONALIDADES,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.callback),
  });
};

export const trackMarketplaceSearchResults = ({
  status,
  searchTerm,
  contentType = "busca",
  screenName = "resultado-da-busca",
} = {}) => {
  sendGA4Event("search", {
    status,
    search_term: searchTerm,
    content_type: contentType,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.search, screenName),
  });
};

export const trackMarketplaceBannerImpression = ({
  index,
  custom_title,
  content_type,
  screenName = "home",
  data_inicio,
  data_termino,
  url_imagem,
} = {}) => {
  sendGA4Event("banner_impression", {
    elemento: "banner-carrossel",
    index,
    custom_title,
    content_type,
    data_inicio,
    data_termino,
    url_imagem,
    ...marketplaceBaseWithScreen(
      MARKETPLACE_TAG_IDS.banner_impression,
      screenName,
    ),
  });
};

export const trackMarketplaceBannerClick = ({
  index,
  custom_title,
  content_type,
  screenName = "home",
  data_inicio,
  data_termino,
  url_imagem,
  url_link,
} = {}) => {
  const params = {
    elemento: "banner-carrossel",
    index,
    custom_title: custom_title ?? "",
    content_type: content_type ?? "home",
    data_inicio: data_inicio ?? "",
    data_termino: data_termino ?? "",
    url_imagem: url_imagem ?? "",
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.banner_click, screenName),
  };

  if (url_link) {
    params.url_link = url_link;
  }

  sendGA4Event("banner_click", params);
};

export const trackViewItemListMarketplace = ({
  itemListName = "",
  items = [],
  coupon = "",
} = {}) => {
  if (!isBrowser || !items.length) return;
  const ecommerceItems = items.map((it, i) =>
    toEcommerceListItem({
      ...it,
      itemListName,
      index: it.index !== undefined ? it.index : i,
      coupon,
    }),
  );
  const value = ecommerceItems.reduce(
    (sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0,
  );
  pushEcommerceWithItems("view_item_list", MARKETPLACE_TAG_IDS.view_item_list, {
    currency: DEFAULT_CURRENCY,
    value,
    items: ecommerceItems,
  });
};

export const trackViewItemListItem = (params = {}) => {
  trackViewItemListMarketplace({
    itemListName: params.itemListName ?? "",
    items: [
      {
        ...params,
        index: params.index ?? 0,
        quantity: params.quantity ?? 1,
      },
    ],
    coupon: params.coupon ?? "",
  });
};

export const trackMarketplaceViewItemListItem = trackViewItemListItem;

export const trackAddToWishlistMarketplace = (params = {}) => {
  if (!isBrowser) return;
  const item = toEcommerceListItem({
    ...params,
    itemListName: params.itemListName ?? "",
    index: params.index ?? 0,
    quantity: params.quantity ?? 1,
    coupon: params.coupon ?? "",
  });
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  pushEcommerceWithItems(
    "add_to_wishlist",
    MARKETPLACE_TAG_IDS.add_to_wishlist,
    {
      currency: DEFAULT_CURRENCY,
      value,
      items: [item],
    },
    { includeTotalPoints: true },
  );
};

export const trackMarketplaceAddToWishlist = trackAddToWishlistMarketplace;

export const trackSelectItemMarketplace = (params = {}) => {
  if (!isBrowser) return;
  const item = toEcommerceListItem({
    ...params,
    itemListName: params.itemListName ?? "",
    index: params.index ?? 0,
    quantity: params.quantity ?? 1,
    coupon: params.coupon ?? "",
  });
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  pushEcommerceWithItems("select_item", MARKETPLACE_TAG_IDS.select_item, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackMarketplaceSelectItem = trackSelectItemMarketplace;

export const trackViewItemMarketplace = (params = {}) => {
  if (!isBrowser) return;
  const item = toEcommerceListItem({
    ...params,
    itemListName: params.itemListName ?? "detalhe-produto",
    index: params.index ?? 0,
    quantity: params.quantity ?? 1,
    coupon: params.coupon ?? "",
  });
  if (item.item_variant) {
    cacheMarketplaceItemVariant(item.item_id, item.item_variant);
    if (params.product) {
      const productId = params.product.uuid ?? params.product.id;
      if (productId) cacheMarketplaceItemVariant(productId, item.item_variant);
    }
  }
  const value = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  pushEcommerceWithItems("view_item", MARKETPLACE_TAG_IDS.view_item, {
    currency: DEFAULT_CURRENCY,
    value,
    items: [item],
  });
};

export const trackMarketplaceViewItem = trackViewItemMarketplace;

export const trackMarketplaceAddToCart = (params = {}) => {
  if (!isBrowser) return;
  const { value: valueParam, price, quantity = 1 } = params;
  const priceNum = Number(price) ?? 0;
  const qty = Number(quantity) || 1;
  const value =
    valueParam !== undefined && valueParam !== null
      ? Number(valueParam)
      : priceNum * qty;

  const item = toEcommerceItemFromParams(params);
  if (item.item_variant) {
    cacheMarketplaceItemVariant(item.item_id, item.item_variant);
    if (params.itemId && String(params.itemId) !== String(item.item_id)) {
      cacheMarketplaceItemVariant(params.itemId, item.item_variant);
    }
    if (params.product) {
      const productId = params.product.uuid ?? params.product.id;
      if (productId) cacheMarketplaceItemVariant(productId, item.item_variant);
    }
  }

  pushEcommerceWithItems(
    "add_to_cart",
    MARKETPLACE_TAG_IDS.add_to_cart,
    {
      currency: DEFAULT_CURRENCY,
      value,
      items: [item],
    },
    { includeTotalPoints: true },
  );
};

export const trackAddToCartFromProduct = trackMarketplaceAddToCart;

export const trackViewCartMarketplaceItems = (items = [], options = {}) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { coupon = "" } = options;
  const ecommerceItems = items.map((item, i) =>
    toEcommerceItemFromCartItem(item, i, coupon),
  );
  const value = ecommerceItems.reduce(
    (sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0,
  );
  pushEcommerceWithItems(
    "view_cart",
    MARKETPLACE_TAG_IDS.view_cart,
    {
      currency: DEFAULT_CURRENCY,
      value,
      items: ecommerceItems,
    },
    { includeTotalPoints: true },
  );
};

export const trackMarketplaceRemoveFromCart = (params = {}) => {
  if (!isBrowser) return;
  const { price, quantity = 1, value: itemValue } = params;
  const priceNum = Number(price) ?? 0;
  const qty = Number(quantity) || 1;
  const value =
    itemValue !== undefined && itemValue !== null
      ? Number(itemValue)
      : priceNum * qty;

  const item = toEcommerceItemFromParams(params);

  pushEcommerceWithItems(
    "remove_from_cart",
    MARKETPLACE_TAG_IDS.remove_from_cart,
    {
      currency: DEFAULT_CURRENCY,
      value,
      items: [item],
    },
    { includeTotalPoints: true },
  );
};

export const trackMarketplaceBeginCheckoutItem = ({
  value,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
} = {}) => {
  sendGA4Event("begin_checkout", {
    currency,
    value: normalizeNumber(value),
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: 1,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.begin_checkout),
  });
};

export const trackMarketplaceAddShippingInfoItem = ({
  value,
  shippingTier,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
} = {}) => {
  sendGA4Event("add_shipping_info", {
    currency,
    value: normalizeNumber(value),
    shipping_tier: shippingTier,
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: 1,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.add_shipping_info),
  });
};

export const trackMarketplaceAddPaymentInfoItem = ({
  value,
  paymentType,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  price,
  currency = DEFAULT_CURRENCY,
  quantity = 1,
} = {}) => {
  sendGA4Event("add_payment_info", {
    currency,
    value: normalizeNumber(value),
    payment_type: paymentType,
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: Number(quantity) || 1,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.add_payment_info),
  });
};

export const trackMarketplacePurchaseItem = ({
  value,
  transactionId,
  shipping,
  coupon,
  itemId,
  itemName,
  affiliation = DEFAULT_AFFILIATION,
  discount,
  index,
  itemBrand,
  itemCategory,
  itemVariant = "",
  points,
  price,
  currency = DEFAULT_CURRENCY,
} = {}) => {
  const event = {
    currency,
    value: normalizeNumber(value),
    transaction_id: transactionId,
    shipping: normalizeNumber(shipping),
    item_id: itemId,
    item_name: itemName,
    affiliation,
    discount: normalizeNumber(discount),
    index,
    item_brand: itemBrand,
    item_category: itemCategory,
    price: normalizeNumber(price),
    quantity: 1,
    coupon,
    ...marketplaceBase(MARKETPLACE_TAG_IDS.purchase),
  };

  const variant = String(itemVariant ?? "").trim();
  if (variant) event.item_variant = variant;

  const pointsNum = normalizeNumber(points);
  if (pointsNum !== undefined) event.points = pointsNum;

  sendGA4Event("purchase", event);
};

export const trackBannerClick = ({
  index,
  custom_title,
  content_type,
  screenName,
  data_inicio,
  data_termino,
  url_imagem,
  url_link,
}) => {
  const params = {
    elemento: "banner-carrossel",
    index: index + 1,
    custom_title: custom_title || `banner-${index + 1}`,
    content_type: content_type || "",
    data_inicio: data_inicio ?? "",
    data_termino: data_termino ?? "",
    url_imagem: url_imagem ?? "",
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.banner_click, screenName),
  };
  if (url_link) params.url_link = url_link;
  sendGA4Event("banner_click", params);
};

const mapCartItemToTrackingParams = (item, index) => {
  const priceNum = Number(
    item.price?.BRL?.offer ?? item.price?.BRL?.price ?? item.price ?? 0,
  );
  const quantity = Number(item.quantity) || 1;
  return {
    value: priceNum * quantity,
    quantity,
    itemId: String(item.uuid ?? item.sku ?? item.id ?? ""),
    itemName: normalizeForTracking(
      item.short_description ?? item.name ?? item.label ?? "",
    ),
    affiliation: item.affiliation ?? DEFAULT_AFFILIATION,
    discount: normalizeNumber(item.discount),
    index: index + 1,
    itemBrand: normalizeForTracking(item.seller ?? item.brand ?? ""),
    itemCategory: normalizeForTracking(
      item.category ?? item.category_label ?? "",
    ),
    price: priceNum,
    currency: DEFAULT_CURRENCY,
  };
};

const mapCartItemToEcommerceItem = (item, index, coupon = "") =>
  toEcommerceItemFromCartItem(item, index, coupon);

const buildEcommerceItemsAndValue = (items = [], coupon = "") => {
  const ecommerceItems = items.map((item, i) =>
    mapCartItemToEcommerceItem(item, i, coupon),
  );
  const value = ecommerceItems.reduce(
    (sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0,
  );
  return { items: ecommerceItems, value };
};

export const trackBeginCheckoutMarketplaceItems = (
  items = [],
  options = {},
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { coupon = "", value: orderValue } = options;
  const { items: ecommerceItems, value: computedValue } =
    buildEcommerceItemsAndValue(items, coupon);
  const value =
    orderValue !== undefined && orderValue !== null
      ? Number(orderValue)
      : computedValue;

  const ecommerce = {
    currency: DEFAULT_CURRENCY,
    value,
    coupon: coupon || undefined,
    items: ecommerceItems,
  };

  sendMarketplaceEcommerceEvent(
    "begin_checkout",
    MARKETPLACE_TAG_IDS.begin_checkout,
    ecommerce,
  );
};

export const trackAddShippingInfoMarketplaceItems = (
  items = [],
  shippingByBrand = {},
  options = {},
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { coupon = "", value: orderValue } = options;
  const { items: ecommerceItems, value: computedValue } =
    buildEcommerceItemsAndValue(items, coupon);
  const value =
    orderValue !== undefined && orderValue !== null
      ? Number(orderValue)
      : computedValue;

  const firstBrandId = items[0]?.brand_uuid;
  const brandShipping = shippingByBrand[firstBrandId] || {};
  const days =
    brandShipping.shipping_deadline ?? brandShipping.estimatedBusinessDays ?? 0;
  const shipping_tier = `frete-${days}-dias-uteis`;

  const ecommerce = {
    currency: DEFAULT_CURRENCY,
    value,
    coupon: coupon || undefined,
    shipping_tier,
    items: ecommerceItems,
  };

  sendMarketplaceEcommerceEvent(
    "add_shipping_info",
    MARKETPLACE_TAG_IDS.add_shipping_info,
    ecommerce,
  );
};

const normalizeMarketplacePaymentType = (paymentType) => {
  const normalized = String(paymentType || "").toLowerCase();
  if (normalized === "pontos" || normalized === "puntos") return "Points";
  if (normalized.includes("pix")) return "PIX";
  return "Credit Card";
};

export const trackAddPaymentInfoMarketplaceItems = (
  items = [],
  paymentType = "cartao-de-credito",
  options = {},
) => {
  if (!isBrowser || !Array.isArray(items) || items.length === 0) return;
  const { value: orderValue, coupon = "" } = options;
  const payment_type = normalizeMarketplacePaymentType(paymentType);

  const { items: ecommerceItems, value } = buildEcommerceItemsAndValue(
    items,
    coupon,
  );
  const finalValue =
    orderValue !== undefined && orderValue !== null
      ? Number(orderValue)
      : value;

  const ecommerce = {
    currency: DEFAULT_CURRENCY,
    value: finalValue,
    coupon: coupon || undefined,
    payment_type,
    items: ecommerceItems,
  };

  sendMarketplaceEcommerceEvent(
    "add_payment_info",
    MARKETPLACE_TAG_IDS.add_payment_info,
    ecommerce,
  );
};

export const trackPurchaseMarketplaceItems = (
  items = [],
  { transactionId, value: orderValue, shipping = 0, coupon = "" } = {},
) => {
  if (
    !isBrowser ||
    !Array.isArray(items) ||
    items.length === 0 ||
    !transactionId
  )
    return;
  const { items: ecommerceItems, value: computedValue } =
    buildEcommerceItemsAndValue(items, coupon);
  const value =
    orderValue !== undefined && orderValue !== null
      ? Number(orderValue)
      : computedValue;
  const shippingNum = Number(shipping) ?? 0;

  const ecommerce = {
    transaction_id: transactionId,
    currency: DEFAULT_CURRENCY,
    value,
    shipping: shippingNum,
    coupon: coupon || undefined,
    items: ecommerceItems,
  };

  sendMarketplaceEcommerceEvent("purchase", MARKETPLACE_TAG_IDS.purchase, ecommerce);
};

export const trackLoginEvent = ({ method, screenName = "home" }) => {
  sendGA4Event("select_content", {
    elemento: "botao",
    method: method,
    ...marketplaceBaseWithScreen(MARKETPLACE_TAG_IDS.login, screenName),
  });
};
